import { toEpochMillis } from './date.helpers';
import { cmToFeetInches, feetInchesToCm, kgToLbs, lbsToKg } from './conversion.helpers';
import { roundTo } from './number.helpers';
import { MeasurementSystem } from '../types';

// Editor field values for one measurement, in the editor's active unit system.
// `inches` is only meaningful for height in imperial (feet + inches); every
// other case leaves it blank and reads only `value`.
export interface IMilestoneInput {
  value: string;
  inches: string;
}

// Metric value persisted for `type`: integer cm for height, 1-decimal kg for
// weight. Used for both the recorded value and change detection, so re-saving an
// unchanged field produces no new record.
export const roundMilestoneForStorage = (type: PlayerMilestoneType, metricValue: number): number =>
  type === 'height' ? Math.round(metricValue) : roundTo(metricValue, 1);

export const isMilestoneInputEmpty = (type: PlayerMilestoneType, input: IMilestoneInput, system: MeasurementSystem): boolean =>
  type === 'height' && system === 'imperial'
    ? !input.value.trim() && !input.inches.trim()
    : !input.value.trim();

// Imperial editor fields for a stored metric value: whole lbs for weight, feet +
// inches for height.
export const metricToImperial = (type: PlayerMilestoneType, metricValue: number): IMilestoneInput => {
  if (type === 'weight') return { value: String(Math.round(kgToLbs(metricValue))), inches: '' };
  const { feet, inches } = cmToFeetInches(metricValue);
  return { value: String(feet), inches: String(inches) };
};

// Stored metric value from imperial editor fields (NaN when blank, negative, or
// unparseable). Empty inches counts as 0 so "5 ft" alone is valid.
export const imperialToMetric = (type: PlayerMilestoneType, input: IMilestoneInput): number => {
  if (type === 'weight') {
    const lbs = Number(input.value);
    return !input.value.trim() || Number.isNaN(lbs) || lbs < 0 ? NaN : lbsToKg(lbs);
  }
  const feet = Number(input.value || '0');
  const inches = Number(input.inches || '0');
  if (Number.isNaN(feet) || Number.isNaN(inches) || feet < 0 || inches < 0) return NaN;
  return feetInchesToCm(feet, inches);
};

// Segments of the dual-unit display value, with the unit tokens (cm/kg/lbs)
// flagged so callers can emphasise them; imperial height carries no word unit.
export interface IMilestoneSegment {
  text: string;
  unit?: boolean;
}

export const milestoneDisplaySegments = (type: PlayerMilestoneType, metricValue: number): IMilestoneSegment[] => {
  if (type === 'weight') {
    return [
      { text: `${roundTo(metricValue, 1)} ` },
      { text: 'kg', unit: true },
      { text: ' / ' },
      { text: `${Math.round(kgToLbs(metricValue))} ` },
      { text: 'lbs', unit: true },
    ];
  }
  const { feet, inches } = cmToFeetInches(metricValue);
  return [
    { text: `${Math.round(metricValue)} ` },
    { text: 'cm', unit: true },
    { text: ` / ${feet}'${inches}"` },
  ];
};

// Read-only "metric / imperial" string, e.g. `180 cm / 5'11"` or `75 kg / 165 lbs`.
export const formatMilestoneDisplay = (type: PlayerMilestoneType, metricValue: number): string =>
  milestoneDisplaySegments(type, metricValue).map((segment) => segment.text).join('');

export interface IMilestoneEvaluation {
  type: PlayerMilestoneType;
  entries: IPlayerMilestone[];
  first: IPlayerMilestone;
  latest: IPlayerMilestone;
  change: number;
}

export const evaluatePlayerMilestones = (milestones: IPlayerMilestone[]): IMilestoneEvaluation[] => {
  const byType = new Map<PlayerMilestoneType, IPlayerMilestone[]>();
  (milestones || []).forEach((milestone) => {
    const list = byType.get(milestone.type) || [];
    list.push(milestone);
    byType.set(milestone.type, list);
  });

  const evaluations: IMilestoneEvaluation[] = [];
  byType.forEach((list, type) => {
    const entries = [...list].sort((a, b) => toEpochMillis(a.recordedAt) - toEpochMillis(b.recordedAt));
    const first = entries[0];
    const latest = entries[entries.length - 1];
    evaluations.push({ type, entries, first, latest, change: latest.value - first.value });
  });

  return evaluations;
};
